/**
 * Pure form logic for the customer + admin commission-rate-schedule editors.
 *
 * The editors are structured forms (no raw JSON): a `RateScheduleDraft` holds
 * string-typed fields while editing, `buildRateScheduleConfig` coerces it into the
 * config JSON the API validates against its product-line shape, and
 * `draftFromConfigRow` is the exact inverse (load a stored schedule back for edit).
 * Kept here — pure, framework-free — so both front-ends share ONE implementation
 * and it's unit-testable (round-trip + schema-validity), independent of React.
 */

export type RateScheduleProductLine = 'major_medical' | 'medicare' | 'ancillary';

// --- form-row shapes (strings while editing; coerced on save). `uid` is a
// client-only stable React key so rows reorder/remove cleanly. ---
export interface MMRate {
  uid: string;
  planCode: string;
  rateType: 'percent_of_premium' | 'flat_per_member';
  rateValue: string; // % for percent, $ for flat
  // --- graded comp (optional; blank = not graded) ---
  minPremium: string; // '' = open lower bound (premium band, $)
  maxPremium: string; // '' = open upper bound (premium band, $)
  compensationType: '' | 'initial' | 'renewal'; // '' = applies to both
}

/** Production-bonus override methods for a graded major-medical schedule. */
export type MMOverrideMethod =
  | ''
  | 'percent_of_premium'
  | 'percent_of_commission'
  | 'flat_per_member'
  | 'per_member_per_month';
export interface CmsRow {
  uid: string;
  contractYear: string;
  productType: 'MA' | 'MAPD' | 'PDP';
  region: string; // '' = national
  compensationType: 'initial' | 'renewal';
  annualAmount: string; // $
}
export interface YearRate {
  uid: string;
  fromYear: string;
  toYear: string; // '' = open-ended
  rate: string; // %
}
/** A dimensioned ancillary cell: policy-year rates applied only to records
 *  matching its state / issue-age / product (blank dimension = any). */
export interface RateBand {
  uid: string;
  state: string; // '' = any state
  ageBand: '' | 'under_65' | '65_plus'; // '' = any age
  planPattern: string; // '' = any product
  yearRates: YearRate[];
}

export interface RateScheduleDraft {
  line: RateScheduleProductLine;
  workspaceId: string;
  carrierId: string;
  mm: MMRate[];
  /** Optional graded major-medical production bonus ('' method = no bonus). */
  mmOverrideMethod: MMOverrideMethod;
  mmOverrideRate: string; // % for the percent methods, $ for flat / PMPM
  cms: CmsRow[];
  rdeMonths: string;
  /** How the carrier pays the CMS base: lump (annual_upfront) or monthly installments. */
  medicareCadence: 'annual_upfront' | 'monthly';
  family: string;
  /** Ancillary rate mode: one flat band set, or dimensioned cells by
   *  state × issue-age × product (the real-world supplemental matrix). */
  ancMode: 'flat' | 'dimensioned';
  yearRates: YearRate[];
  bands: RateBand[];
  under65Pattern: string; // ageClassification.under65PlanPattern
  method: 'as_earned' | 'advanced';
  advanceFraction: string;
}

/** The minimal stored-row shape `draftFromConfigRow` needs (structural). */
export interface RateScheduleConfigRow {
  productLine: RateScheduleProductLine;
  workspaceId: string | null;
  carrierId: string | null;
  config: Record<string, unknown>;
}

let _seq = 0;
const k = () => `row-${_seq++}`;
export const mmRow = (p: Partial<MMRate> = {}): MMRate => ({
  uid: k(),
  planCode: '',
  rateType: 'percent_of_premium',
  rateValue: '',
  minPremium: '',
  maxPremium: '',
  compensationType: '',
  ...p,
});
export const cmsRow = (p: Partial<CmsRow> = {}): CmsRow => ({
  uid: k(),
  contractYear: '2026',
  productType: 'MAPD',
  region: '',
  compensationType: 'initial',
  annualAmount: '',
  ...p,
});
export const yrRow = (p: Partial<YearRate> = {}): YearRate => ({
  uid: k(),
  fromYear: '',
  toYear: '',
  rate: '',
  ...p,
});
export const bandRow = (p: Partial<RateBand> = {}): RateBand => ({
  uid: k(),
  state: '',
  ageBand: '',
  planPattern: '',
  yearRates: [yrRow({ fromYear: '1', rate: '' })],
  ...p,
});

export function emptyRateScheduleDraft(): RateScheduleDraft {
  return {
    line: 'medicare',
    workspaceId: '',
    carrierId: '',
    mm: [mmRow({ rateValue: '20' })],
    mmOverrideMethod: '',
    mmOverrideRate: '',
    cms: [
      cmsRow({ compensationType: 'initial', annualAmount: '694' }),
      cmsRow({ compensationType: 'renewal', annualAmount: '347' }),
    ],
    rdeMonths: '3',
    medicareCadence: 'annual_upfront',
    family: 'med_supp',
    ancMode: 'flat',
    yearRates: [yrRow({ fromYear: '1', rate: '18' })],
    bands: [
      bandRow({ state: 'WI', ageBand: '65_plus', planPattern: 'medicare supplement|med supp' }),
    ],
    under65Pattern: '\\bDIS\\b|DISABILITY',
    method: 'as_earned',
    advanceFraction: '1',
  };
}

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const toYearRates = (rows: YearRate[]) =>
  rows.map((r) => ({
    fromYear: Number(r.fromYear),
    toYear: numOrNull(r.toYear),
    rate: Number(r.rate) / 100,
  }));

/** Build the config JSON the API validates against its product-line shape. */
export function buildRateScheduleConfig(d: RateScheduleDraft): Record<string, unknown> {
  if (d.line === 'major_medical') {
    // Percent override methods store a fraction; flat / PMPM store dollars.
    const overridePercent =
      d.mmOverrideMethod === 'percent_of_premium' || d.mmOverrideMethod === 'percent_of_commission';
    return {
      productLine: 'major_medical',
      rates: d.mm.map((r) => ({
        planCode: r.planCode.trim() || null,
        rateType: r.rateType,
        rateValue:
          r.rateType === 'percent_of_premium' ? Number(r.rateValue) / 100 : Number(r.rateValue),
        // Graded dimensions are emitted only when set (kept absent otherwise so a
        // simple schedule stays byte-identical to the pre-graded shape).
        ...(r.minPremium.trim() !== '' ? { minPremium: Number(r.minPremium) } : {}),
        ...(r.maxPremium.trim() !== '' ? { maxPremium: Number(r.maxPremium) } : {}),
        ...(r.compensationType ? { compensationType: r.compensationType } : {}),
      })),
      ...(d.mmOverrideMethod
        ? {
            override: {
              method: d.mmOverrideMethod,
              rate: overridePercent ? Number(d.mmOverrideRate) / 100 : Number(d.mmOverrideRate),
            },
          }
        : {}),
    };
  }
  if (d.line === 'medicare') {
    return {
      productLine: 'medicare',
      cmsTable: d.cms.map((r) => ({
        contractYear: Number(r.contractYear),
        productType: r.productType,
        region: r.region.trim() || null,
        compensationType: r.compensationType,
        annualAmount: Number(r.annualAmount),
      })),
      rdeMonths: Number(d.rdeMonths),
      ...(d.medicareCadence === 'monthly' ? { paymentCadence: 'monthly' } : {}),
    };
  }
  const base = {
    productLine: 'ancillary',
    productFamily: d.family.trim() || 'ancillary',
    method: d.method,
    ...(d.method === 'advanced' ? { advanceFraction: Number(d.advanceFraction) } : {}),
  };
  if (d.ancMode === 'dimensioned') {
    const anyAge = d.bands.some((b) => b.ageBand !== '');
    return {
      ...base,
      rateBands: d.bands.map((b) => ({
        ...(b.state.trim() ? { state: b.state.trim().toUpperCase() } : {}),
        ...(b.ageBand ? { ageBand: b.ageBand } : {}),
        ...(b.planPattern.trim() ? { planPattern: b.planPattern.trim() } : {}),
        yearRates: toYearRates(b.yearRates),
      })),
      ...(anyAge && d.under65Pattern.trim()
        ? { ageClassification: { under65PlanPattern: d.under65Pattern.trim() } }
        : {}),
    };
  }
  return { ...base, yearRates: toYearRates(d.yearRates) };
}

/** Inverse of `buildRateScheduleConfig`: load a stored config back into a draft. */
export function draftFromConfigRow(row: RateScheduleConfigRow): RateScheduleDraft {
  const d = emptyRateScheduleDraft();
  d.line = row.productLine;
  d.workspaceId = row.workspaceId ?? '';
  d.carrierId = row.carrierId ?? '';
  const c = row.config;
  const toYrRows = (rows: unknown): YearRate[] =>
    (Array.isArray(rows) ? (rows as Record<string, unknown>[]) : []).map((r) =>
      yrRow({
        fromYear: String(r.fromYear),
        toYear: r.toYear == null ? '' : String(r.toYear),
        rate: String(Number(r.rate) * 100),
      }),
    );
  if (row.productLine === 'major_medical' && Array.isArray(c.rates)) {
    d.mm = (c.rates as Record<string, unknown>[]).map((r) =>
      mmRow({
        planCode: (r.planCode as string) ?? '',
        rateType: r.rateType as MMRate['rateType'],
        rateValue: String(
          r.rateType === 'percent_of_premium' ? Number(r.rateValue) * 100 : r.rateValue,
        ),
        minPremium: r.minPremium == null ? '' : String(r.minPremium),
        maxPremium: r.maxPremium == null ? '' : String(r.maxPremium),
        compensationType: (r.compensationType as MMRate['compensationType']) ?? '',
      }),
    );
    const ov = c.override as Record<string, unknown> | undefined;
    if (ov && typeof ov.method === 'string') {
      d.mmOverrideMethod = ov.method as MMOverrideMethod;
      const pct = ov.method === 'percent_of_premium' || ov.method === 'percent_of_commission';
      d.mmOverrideRate = String(pct ? Number(ov.rate) * 100 : ov.rate);
    }
  } else if (row.productLine === 'medicare' && Array.isArray(c.cmsTable)) {
    d.cms = (c.cmsTable as Record<string, unknown>[]).map((r) =>
      cmsRow({
        contractYear: String(r.contractYear),
        productType: r.productType as CmsRow['productType'],
        region: (r.region as string) ?? '',
        compensationType: r.compensationType as CmsRow['compensationType'],
        annualAmount: String(r.annualAmount),
      }),
    );
    d.rdeMonths = String(c.rdeMonths ?? 3);
    d.medicareCadence = c.paymentCadence === 'monthly' ? 'monthly' : 'annual_upfront';
  } else if (row.productLine === 'ancillary') {
    d.family = (c.productFamily as string) ?? '';
    d.method = (c.method as RateScheduleDraft['method']) ?? 'as_earned';
    d.advanceFraction = String(c.advanceFraction ?? 1);
    if (Array.isArray(c.rateBands)) {
      d.ancMode = 'dimensioned';
      d.bands = (c.rateBands as Record<string, unknown>[]).map((b) =>
        bandRow({
          state: (b.state as string) ?? '',
          ageBand: (b.ageBand as RateBand['ageBand']) ?? '',
          planPattern: (b.planPattern as string) ?? '',
          yearRates: toYrRows(b.yearRates),
        }),
      );
      const ac = c.ageClassification as Record<string, unknown> | undefined;
      d.under65Pattern = (ac?.under65PlanPattern as string) ?? '';
    } else if (Array.isArray(c.yearRates)) {
      d.ancMode = 'flat';
      d.yearRates = toYrRows(c.yearRates);
    }
  }
  return d;
}

/** One-line summary of a stored schedule for the list view. */
export function summarizeRateSchedule(row: RateScheduleConfigRow): string {
  const c = row.config;
  if (row.productLine === 'medicare') {
    const n = Array.isArray(c.cmsTable) ? c.cmsTable.length : 0;
    return `CMS table · ${n} rate row${n === 1 ? '' : 's'}`;
  }
  if (row.productLine === 'ancillary') {
    const fam = typeof c.productFamily === 'string' ? c.productFamily : 'ancillary';
    const method = typeof c.method === 'string' ? c.method : '';
    const nb = Array.isArray(c.rateBands) ? c.rateBands.length : 0;
    return nb > 0 ? `${fam} · ${nb} band${nb === 1 ? '' : 's'} · ${method}` : `${fam} · ${method}`;
  }
  const rates = Array.isArray(c.rates) ? (c.rates as Record<string, unknown>[]) : [];
  const n = rates.length;
  const graded = rates.some(
    (r) => r.minPremium != null || r.maxPremium != null || r.compensationType != null,
  );
  const bonus = c.override != null;
  const tags = [graded ? 'graded' : null, bonus ? '+ bonus' : null].filter(Boolean).join(' ');
  return `${n} rate${n === 1 ? '' : 's'}${tags ? ` · ${tags}` : ''}`;
}

// CMS sets a national maximum plus higher caps for a few states/territories; the
// ancillary matrix also keys by state. Offered as a datalist typeahead in the UI.
export const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
];
